import { useEffect, useState } from 'react';
import { Search, Download, Scissors, Eye, Edit, PauseCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { fetchPackageAdminList } from '@/api/Admin/userPackage';
import { formatCurrency } from './StatusBadge';

interface MiniPackage {
    id: number;
    code?: string;
    name: string;
    description?: string;
    price: string;
    total_slots: number;
    used_slots: number;
    sold_slots: number;
    remaining_slots?: number;
    monthly_commission_rate: string;
    commission_rate: string;
    duration_months: number;
    min_profit_rate: string;
    is_active: boolean;
    type: string;
    created_at?: string;
    parent?: {
        id: number;
        name: string;
        remaining_slots: number;
    };
}

export const MiniPackageTab = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [packages, setPackages] = useState<MiniPackage[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
        setLoading(true);
        fetchPackageAdminList()
            .then(data => {
                const all = data.data.data as unknown as MiniPackage[];
                // Mini packages are those that have a parent reference
                const mini = all.filter(p => p.parent != null || p.type === 'mini');
                setPackages(mini);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const filtered = packages.filter(p => {
        const matchSearch = !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus =
            !statusFilter ||
            (statusFilter === 'active' ? p.is_active : !p.is_active);
        return matchSearch && matchStatus;
    });

    return (
        <div>
            {/* Sub-header for mini tab */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t('plan.miniTitle', 'Quản lý Gói mini')}
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">
                        {t('plan.miniSubtitle', 'Các gói mini được tách từ gói TITAN/PIONEER')}
                    </p>
                </div>
                <button
                    onClick={() => navigate('split-package')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-medium text-sm shadow-sm transition-colors"
                >
                    <Scissors className="h-4 w-4" />
                    {t('plan.splitPackage')}
                </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700">
                {/* Toolbar */}
                <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                className="block w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-colors"
                                placeholder={t('plan.searchPlaceholder')}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <select
                                className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                            >
                                <option value="">{t('plan.allStatuses')}</option>
                                <option value="active">{t('plan.statusActive')}</option>
                                <option value="inactive">{t('plan.statusInactive')}</option>
                            </select>
                            <button className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-white rounded-md text-sm font-medium transition-colors">
                                <Download className="h-4 w-4" />
                                {t('plan.exportExcel')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto relative min-h-[200px]">
                    {loading && (
                        <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 z-10 flex items-center justify-center">
                            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/50">
                            <tr>
                                {[
                                    'MÃ GÓI',
                                    'TÊN GÓI',
                                    'LOẠI',
                                    'SLOTS',
                                    'MỆNH GIÁ/SLOT',
                                    'TỔNG GIÁ TRỊ',
                                    'LÃI SUẤT',
                                    'THỜI HẠN',
                                    'NĐT',
                                    'TRẠNG THÁI',
                                    'HÀNH ĐỘNG',
                                ].map(h => (
                                    <th
                                        key={h}
                                        className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {!loading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={11} className="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                                        {t('plan.table.noData')}
                                    </td>
                                </tr>
                            )}
                            {filtered.map(pkg => {
                                const soldSlots = pkg.sold_slots || 0;
                                const totalSlots = pkg.total_slots || 0;
                                const remainingSlots = totalSlots - soldSlots;
                                const slotPercentage = totalSlots > 0 ? (soldSlots / totalSlots) * 100 : 0;
                                const totalValue = parseFloat(pkg.price) * totalSlots;
                                const soldValue = parseFloat(pkg.price) * soldSlots;
                                
                                return (
                                    <tr key={pkg.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        {/* MÃ GÓI */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-bold text-gray-900 dark:text-white">
                                                {/* chưa có code trong res nên placeholder PKF-id */}
                                                {pkg.code || `PKG-${pkg.id}`}
                                            </div>
                                            {pkg.created_at && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                    {new Date(pkg.created_at).toLocaleDateString('vi-VN')}
                                                </div>
                                            )}
                                        </td>

                                        {/* TÊN GÓI */}
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-bold text-gray-900 dark:text-white">
                                                {pkg.name}
                                            </div>
                                            {pkg.description && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs truncate">
                                                    {pkg.description}
                                                </div>
                                            )}
                                        </td>

                                        {/* LOẠI */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                                {pkg.type?.toUpperCase() || 'MINI'}
                                            </span>
                                        </td>

                                        {/* SLOTS */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-bold text-gray-900 dark:text-white">
                                                {soldSlots}/{totalSlots}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {slotPercentage.toFixed(0)}%
                                            </div>
                                            <div className="flex items-center gap-1 mt-1">
                                                <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                                                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                                    Còn {remainingSlots}
                                                </span>
                                            </div>
                                        </td>

                                        {/* MỆNH GIÁ/SLOT */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-bold text-gray-900 dark:text-white">
                                                {formatCurrency(pkg.price)}
                                            </div>
                                        </td>

                                        {/* TỔNG GIÁ TRỊ */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                                {formatCurrency(totalValue.toString())}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Đã bán: {formatCurrency(soldValue.toString())}
                                            </div>
                                        </td>

                                        {/* LÃI SUẤT */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {parseFloat(pkg.min_profit_rate) > 0
                                                    ? `${pkg.min_profit_rate}%`
                                                    : pkg.monthly_commission_rate + '%'
                                                }
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">/ tháng</div>
                                        </td>

                                        {/* THỜI HẠN */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {pkg.duration_months === 0
                                                    ? 'Không giới hạn'
                                                    : `${pkg.duration_months} tháng`
                                                }
                                            </div>
                                        </td>

                                        {/* Số NĐT */}
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {pkg.sold_slots || 0}
                                            </div>
                                        </td>

                                        {/* TRẠNG THÁI */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                {pkg.is_active ? (
                                                    <>
                                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                                        <span className="text-sm text-gray-900 dark:text-white">Hoạt động</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <PauseCircle className="h-4 w-4 text-yellow-500" />
                                                        <span className="text-sm text-gray-900 dark:text-white">Tạm dừng</span>
                                                    </>
                                                )}
                                            </div>
                                        </td>

                                        {/* HÀNH ĐỘNG */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => navigate(`/admin/plan/${pkg.id}`)}
                                                    className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                                                    title="Xem chi tiết"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/admin/plan/${pkg.id}/edit`)}
                                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                                                    title="Chỉnh sửa"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
